import type { DeepPartial } from "react-hook-form";

type RecursiveDirtyField = boolean | undefined | { [key: string]: RecursiveDirtyField } | RecursiveDirtyField[];

const getDirtyValuesRecursive = <T>(dirtyFields: RecursiveDirtyField, values: T): DeepPartial<T> | undefined => {
  if (!dirtyFields || values === undefined) return undefined;
  if (dirtyFields === true) return values as DeepPartial<T>;

  if (Array.isArray(dirtyFields)) {
    if (!Array.isArray(values)) return undefined;
    const dirtyArray = dirtyFields.map((d, i) => getDirtyValuesRecursive(d, values[i]));
    const hasChanges = dirtyArray.some(item => item !== undefined);
    return hasChanges ? (dirtyArray as DeepPartial<T>) : undefined;
  }

  if (typeof dirtyFields === "object") {
    const dirtyObject: Record<string, unknown> = {};
    const dirtyFieldsObj = dirtyFields;
    const valuesObj = values as Record<string, unknown>;

    Object.keys(dirtyFieldsObj).forEach(key => {
      const itemDirty = dirtyFieldsObj[key];
      const itemValue = valuesObj[key];
      const dirtyResult = getDirtyValuesRecursive(itemDirty, itemValue);
      if (dirtyResult !== undefined) dirtyObject[key] = dirtyResult;
    });

    return Object.keys(dirtyObject).length > 0 ? (dirtyObject as DeepPartial<T>) : undefined;
  }

  return undefined;
};

const getDirtyValues = <T>(dirtyFields: RecursiveDirtyField, values: T): DeepPartial<T> => {
  const result = getDirtyValuesRecursive(dirtyFields, values);
  if (result === undefined) return (Array.isArray(values) ? [] : {}) as DeepPartial<T>;
  return result;
};

export { getDirtyValues };
